import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import type {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {AgeCategoryService} from "crqlar-react-common/dist/services/ageCategory/ageCategory.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const AgeCategoriesList = () => {
    useTrackScreenVisit('system age categories');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<AgeCategory>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: ageCategory => ageCategory.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'minAge',
            header: t('Min age'),
            accessorFn: ageCategory => ageCategory.minAge,
            cell: ({row}) => <Box>{row.original.minAge || '0'}</Box>,
            size: 120,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'maxAge',
            header: t('Max age'),
            accessorFn: ageCategory => ageCategory.maxAge,
            cell: ({row}) => <Box>{row.original.maxAge || 'N/A'}</Box>,
            size: 120,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'type',
            header: t('Age category type'),
            accessorFn: ageCategory => ageCategory.type,
            cell: ({row}) => <Box>{row.original.type ? t(`Age category type ${row.original.type}`) : null}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'alias',
            header: t('Alias'),
            accessorFn: ageCategory => ageCategory.alias,
            cell: ({row}) => <Box>{row.original.alias || null}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: ageCategory => ageCategory.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: ageCategory => ageCategory.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-age-categories',
        columns,
        getRowId: ageCategory => ageCategory.id!,
        onRowClick: ageCategory => navigate(`/system/ageCategories/${ageCategory.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await AgeCategoryService.getAllAgeCategories({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('Age categories')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create Age Category')}
                            onClick={() => navigate('/system/ageCategories/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default AgeCategoriesList;
